import type * as React from "react";
import { useState, useEffect } from "react";

// Third Party
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useNavigate, createFileRoute } from "@tanstack/react-router";

// UI
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Card,
	CardHeader,
	CardTitle,
	CardDescription,
	CardContent,
} from "@/components/ui/card";
import { ArrowLeft, Save, Trash2, CirclePlus } from "lucide-react";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { FileUpload } from "@/components/FileUpload";
import { Spinner } from "@/components/ui/spinner";

// Services
import { getEquities, editEquities } from "@/services/finance";
import { cn } from "@/lib/utils";
import DinamixSkeletonTable from "@/components/skeleton/dinamixTableSkeleton";



export const Route = createFileRoute(
	"/_layout-private/report/finance/company/_layout/owner-equity/edit",
)({
	component: EditOwnerEquity,
});

function EditOwnerEquity() {
	const { toast } = useToast();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const companyId = localStorage.getItem("companyId");
	const year = Number(localStorage.getItem("year"));
	const [docFile, setDocFile] = useState("");

	// Fetch data from backend
	const { data, isLoading, error } = useQuery({
		queryKey: ["equities", companyId, year],
		queryFn: () => getEquities(companyId as string, year),
		enabled: Boolean(companyId && year),
	});

	const mutation = useMutation({
		mutationFn: async (values) => {
			try {
				await editEquities(companyId, {
					companyId,
					yearly: year,
					docFile: values.docFile,
					equityDetails: values.equityDetails,
				});
			} catch (error) {
				console.error("Error submitting data:", error);
				throw error;
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["equities"] });
			navigate({ to: "/report/finance/company/owner-equity" });
			toast({
				title: "ສຳເລັດ",
				description: "ບັນທຶກຂໍ້ມູນສຳເລັດ",
				variant: "success",
			});
		},
		onError: (error) => {
			toast({
				title: "ບໍ່ສຳເລັດ",
				description: "ບັນທຶກຂໍ້ມູນບໍ່ສຳເລັດ",
			});
		},
	});

	const [initialNotCurrentYearEquity, setInitialNotCurrentYearEquity] =
		useState([]);
	const [initialCurrentYearEquity, setInitialCurrentYearEquity] = useState([]);

	useEffect(() => {
		if (data?.data) {
			const equityDetails = data.data.equityDetails;

			const initialNotCurrentYearEquity = equityDetails?.filter(
				(item) => item?.isCurrentYear === false,
			);

			const initialCurrentYearEquity = equityDetails?.filter(
				(item) => item?.isCurrentYear === true,
			);

			setDocFile(data.data.docFile);
			setInitialCurrentYearEquity(initialCurrentYearEquity);
			setInitialNotCurrentYearEquity(initialNotCurrentYearEquity);
		}
	}, [data]);

	// Add a new row with default numeric values


	/**
 * Format a number with commas and handle negative numbers with parentheses
 * @param value Number or string to format
 * @returns Formatted string with commas and appropriate negative representation
 */
	function formatNumberWithCommas(value: string | number) {
		// Handle empty values, but allow explicit zero
		if (value === undefined || value === null || value === "") return "";

		// Convert to string and handle basic cases
		const stringValue = String(value);

		// Preserve special input states during typing
		if (stringValue === "." || stringValue === "-") return stringValue;
		if (stringValue === "(") return "(";
		if (stringValue === "()") return "()";

		// CRITICAL FIX: Preserve partial parentheses during typing
		// If we have an opening parenthesis without closing one, consider it as starting a negative
		if (stringValue.startsWith("(") && !stringValue.endsWith(")")) {
			const numericPart = stringValue.substring(1);

			// Handle special case of just opening paren with decimal point
			if (numericPart === ".") return "(.";

			// Format the inner part with commas if needed
			const formatted = formatInnerNumber(numericPart);
			return `(${formatted}`;
		}

		// Determine if negative by either minus sign or complete parentheses
		const isNegative = stringValue.startsWith("-") ||
			(stringValue.startsWith("(") && stringValue.endsWith(")"));

		// Remove any negative indicators for processing
		const cleanValue = stringValue.replace(/[()-]/g, "");

		// Format the inner part
		const formatted = formatInnerNumber(cleanValue);

		// Apply negative formatting if needed (using parentheses)
		return isNegative ? `(${formatted})` : formatted;
	}

	/**
	 * Helper function to format the inner part of a number (without negative signs)
	 */
	function formatInnerNumber(value: string) {
		// Handle empty or just decimal point
		if (!value || value === ".") return value;

		// Handle just typing a decimal point at the end
		if (value.endsWith(".")) {
			const integerPart = value.slice(0, -1);
			const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
			return `${formattedInteger}.`;
		}

		// Split into integer and decimal parts
		const [integerPart, decimalPart] = value.split(".");

		// Format integer part with commas
		const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

		// Handle decimal part (limit to 2 places)
		const formattedDecimal = decimalPart ? decimalPart.slice(0, 2) : undefined;

		// Rebuild the number with proper decimal formatting
		return formattedDecimal !== undefined
			? `${formattedInteger}.${formattedDecimal}`
			: formattedInteger;
	}

	/**
	 * Parse a number string with potential parentheses notation for negatives
	 * @param strValue String or number to parse
	 * @returns Number value (will be negative if wrapped in parentheses)
	 */
	function parseParenthesisNumber(strValue: string | number) {
		if (strValue === undefined || strValue === null || strValue === "") return 0;

		// Convert to string and remove commas
		const stringValue = String(strValue).replace(/,/g, "");

		// Handle incomplete parentheses
		if (stringValue === "(" || stringValue === "()") return 0;

		// Handle incomplete negative numbers (with opening parenthesis only)
		if (stringValue.startsWith("(") && !stringValue.endsWith(")")) {
			// Consider it as a negative number being typed
			const numericPart = stringValue.substring(1);
			const parsed = Number(numericPart) || 0;
			return -parsed; // Return as negative
		}

		// Check for parentheses notation (negative)
		const isNegative = stringValue.startsWith("(") && stringValue.endsWith(")");

		// Remove parentheses if present
		const numericValue = isNegative ? stringValue.slice(1, -1) : stringValue;

		// Parse to number, default to 0 if not a valid number
		const parsed = Number(numericValue) || 0;

		// Return negative value if it was in parentheses
		return isNegative ? -parsed : parsed;
	}

	/**
	 * Handle changes to numeric input fields, allowing parentheses notation
	 * for negative numbers
	 */
	function handleNumericChange({
		e,
		index,
		fieldName,
		data,
		setData,
	}: {
		e: React.ChangeEvent<HTMLInputElement>;
		index: number;
		fieldName: "registeredCapital" | "reserve" | "other" | "accProfitLoss" | "storage" | "expansion";
		data: any[];
		setData: any;
	}) {
		const value = e.target.value;

		// Handle empty field case
		if (value === "") {
			const newData = [...data];
			newData[index][fieldName] = "";
			setData(newData);
			return;
		}

		// Remove commas for validation (we'll add them back when formatting)
		const unformattedValue = value.replace(/,/g, "");

		// VALIDATION PHASE
		// ----------------

		// The following patterns should all be valid during typing:
		// - Regular numbers: "123", "123.45"
		// - Starting to type a negative: "(", "("
		// - Negative numbers: "(123)", "(123.45)"
		// - Partial negative numbers: "(123", "(123."

		// IMPORTANT: Add validation patterns that specifically handle all typing states
		const validPatterns = [
			/^$/, // Empty
			/^\d+$/, // Integers
			/^\d+\.$/, // Integer with decimal point
			/^\d+\.\d{0,2}$/, // Number with up to 2 decimal places
			/^\($/, // Just an opening parenthesis
			/^\(\)$/, // Empty parentheses
			/^\(\d*$/, // Opening parenthesis with digits
			/^\(\d+\)$/, // Complete negative integer
			/^\(\d+\.\)$/, // Negative with decimal point
			/^\(\d+\.\d{0,2}\)$/, // Negative with decimal places
			/^\(\d+\.$/, // Starting negative with decimal
			/^\(\d+\.\d{0,2}$/, // Negative with decimal, no closing paren yet
			/^\(\.$/, // Just open paren with decimal point
			/^\(\.\d{0,2}$/, // Open paren with decimal digits
			/^\(\.\d{0,2}\)$/ // Negative decimal less than 1
		];

		// If the input doesn't match any valid pattern, reject it
		if (!validPatterns.some(pattern => pattern.test(unformattedValue))) {
			return;
		}

		// Update the state with the new value
		const newData = [...data];
		newData[index][fieldName] = unformattedValue;
		setData(newData);
	}


	// Prepare data for submission
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!companyId || !year) {
			toast({
				title: "ຂໍ້ມູນບໍ່ຄົບຖ້ວນ",
				description: "ກະລຸນາປ້ອນຂໍ້ມູນທຶນເຈົ້າຂອງ",
				variant: "destructive",
			});
			return;
		}

		if (!docFile) {
			toast({
				title: "ບັນທຶກລົ້ມເຫຼວ",
				description: "ກະລຸນາອັບໂຫຼດເອກະສານຕິດຂັດ",
				variant: "destructive",
			});
			return;
		}

		// Prepare the data with number parsing
		const notCurrentYear = initialNotCurrentYearEquity.map((row) => ({
			...row,
			registeredCapital: parseParenthesisNumber(row.registeredCapital),
			storage: parseParenthesisNumber(row.storage),
			expansion: parseParenthesisNumber(row.expansion),
			accProfitLoss: parseParenthesisNumber(row.accProfitLoss),
			other: parseParenthesisNumber(row.other),
		}));

		const currentYear = initialCurrentYearEquity.map((row) => ({
			...row,
			registeredCapital: parseParenthesisNumber(row.registeredCapital),
			storage: parseParenthesisNumber(row.storage),
			expansion: parseParenthesisNumber(row.expansion),
			accProfitLoss: parseParenthesisNumber(row.accProfitLoss),
			other: parseParenthesisNumber(row.other),
		}));

		const dataToSubmit = {
			companyId,
			yearly: year,
			equityDetails: [...notCurrentYear, ...currentYear],
			docFile: docFile,
		};

		mutation.mutate(dataToSubmit);
	};

	/**
	 * Compute the sum of the row's numeric fields by parsing parentheses-based strings.
	 */
	function computeRowTotal(row: any) {
		const cap = parseParenthesisNumber(row.registeredCapital);
		const str = parseParenthesisNumber(row.storage);
		const exp = parseParenthesisNumber(row.expansion);
		const acc = parseParenthesisNumber(row.accProfitLoss);
		const oth = parseParenthesisNumber(row.other);

		const total = cap + str + exp + acc + oth;
		return total.toLocaleString();
	}

	function computeSumForField(data: any[], fieldName: string) {
		const sum = data.reduce((acc, row) => {
			return acc + parseParenthesisNumber(row[fieldName]);
		}, 0);
		return sum.toLocaleString();
	}

	if (isLoading) return <DinamixSkeletonTable />;
	if (error) return <div>Error loading data</div>;

	return (
		<section>
			<Card className="border-none">
				<CardHeader className="border-b mb-[32px]">
					<button
						type="button"
						onClick={() => navigate({ to: "/report/finance" })}
						className="flex items-center gap-[4px] mb-4"
					>
						<ArrowLeft className="w-5 h-5" />
						ກັບຄືນ
					</button>
					<CardTitle>ແກ້ໄຂຍງານສ່ວນປ່ຽນແປງທຶນເຈົ້າຂອງ</CardTitle>
					<CardDescription>ກະລຸນາປ້ອນຂໍ້ມູນໃຫ້ຄົບຖ້ວນ</CardDescription>
				</CardHeader>

				<CardContent>
					<form onSubmit={handleSubmit}>
						{/* ------------------- Year Input ------------------- */}
						<div className="flex flex-col gap-2 mb-4">
							<Label>ປະຈຳປີ</Label>
							<Input
								type="number"
								value={year}
								disabled
								className="max-w-[300px]"
							/>
						</div>

						{/* ------------------- Equity Table ------------------- */}
						<Table className="overflow-x-auto w-full border table-fixed">
							<TableHeader>
								<TableRow className="[&>*]:border [&>*]:w-[180px]   [&>*]:font-bold [&>*]:text-black">
									<TableHead>ເນຶ້ອໃນລາຍງານ</TableHead>
									<TableHead>ທຶນຈົດທະບຽນ</TableHead>
									<TableHead>ຄັງສະສົມຕາມລະບຽບການ</TableHead>
									<TableHead>ຄັງຂະຫຍາຍການຜະລິດ</TableHead>
									<TableHead>ກຳໄລ-ຂາດທຶນສະສົມ</TableHead>
									<TableHead>ອື່ນ</TableHead>
									<TableHead>ລວມທຶນ</TableHead>
									<TableHead>ໝາຍເຫດ</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody className="">
								{initialNotCurrentYearEquity?.map((equity, index) => (
									<TableRow
										key={index}
										className={cn(index === 0 ? "bg-[#216C4C]/20" : "")}
									>
										{/* ------ Name ------ */}
										<TableCell className="border">
											{/* First 3 rows are “fixed name,” else an input */}
											{[0, 1, 2].includes(index) ? (
												<>{equity.name}</>
											) : (
												<Input
													type="text"
													value={equity.name}
													onChange={(e) => {
														const newRows = [...initialNotCurrentYearEquity];
														newRows[index].name = e.target.value;
														setInitialNotCurrentYearEquity(newRows);
													}}
													required
												/>
											)}
										</TableCell>

										{/* ------ registeredCapital ------ */}
										<TableCell className="border">
											<Input
												type="text"
												value={formatNumberWithCommas(equity.registeredCapital)}
												onChange={(e) =>
													handleNumericChange({
														e,
														index,
														fieldName: "registeredCapital",
														data: initialNotCurrentYearEquity,
														setData: setInitialNotCurrentYearEquity,
													})
												}
												inputMode="numeric"
												className="text-right"
											// required
											/>
										</TableCell>

										{/* ------ storage ------ */}
										<TableCell className="border">
											<Input
												type="text"
												value={formatNumberWithCommas(equity.storage)}
												onChange={(e) =>
													handleNumericChange({
														e,
														index,
														fieldName: "storage",
														data: initialNotCurrentYearEquity,
														setData: setInitialNotCurrentYearEquity,
													})
												}
												inputMode="numeric"
												// required
												className="text-right"
											/>
										</TableCell>

										{/* ------ expansion ------ */}
										<TableCell className="border">
											<Input
												type="text"
												value={formatNumberWithCommas(equity.expansion)}
												onChange={(e) =>
													handleNumericChange({
														e,
														index,
														fieldName: "expansion",
														data: initialNotCurrentYearEquity,
														setData: setInitialNotCurrentYearEquity,
													})
												}
												inputMode="numeric"
												className="text-right"

											// required
											/>
										</TableCell>

										{/* ------ accProfitLoss ------ */}
										<TableCell className="border">
											<Input
												type="text"
												value={formatNumberWithCommas(equity.accProfitLoss)}
												onChange={(e) =>
													handleNumericChange({
														e,
														index,
														fieldName: "accProfitLoss",
														data: initialNotCurrentYearEquity,
														setData: setInitialNotCurrentYearEquity,
													})
												}
												inputMode="numeric"
												className="text-right"

											// required
											/>
										</TableCell>

										{/* ------ Other ------ */}
										<TableCell className="border">
											<Input
												type="text"
												value={formatNumberWithCommas(equity.other)}
												onChange={(e) =>
													handleNumericChange({
														e,
														index,
														fieldName: "other",
														data: initialNotCurrentYearEquity,
														setData: setInitialNotCurrentYearEquity,
													})
												}
												inputMode="numeric"
												className="text-right"

											// required
											/>
										</TableCell>

										{/* ------ sum of all four fields ------ */}
										<TableCell className="text-right border">
											{computeRowTotal(equity)}
										</TableCell>

										{/* ------ remark ------ */}
										<TableCell className="border">
											<Input
												value={equity.remark}
												onChange={(e) => {
													const newRows = [...initialNotCurrentYearEquity];
													newRows[index].remark = e.target.value;
													setInitialNotCurrentYearEquity(newRows);
												}}
											/>
										</TableCell>
									</TableRow>
								))}

								{/* ------ Some fixed rows below ------ */}
								<TableRow className="bg-[#216C4C]/20">
									<TableCell className="border">
										II. ປັບປຸງຍອດຄືນໃຫມ່ 1 ມັງກອນ {year}
									</TableCell>
									<TableCell className="text-right border">
										{formatNumberWithCommas(
											computeSumForField(
												initialNotCurrentYearEquity,
												"registeredCapital",
											),
										)}
									</TableCell>
									<TableCell className="text-right border">
										{formatNumberWithCommas(
											computeSumForField(
												initialNotCurrentYearEquity,
												"storage",
											),
										)}
									</TableCell>
									<TableCell className="text-right border">
										{formatNumberWithCommas(
											computeSumForField(
												initialNotCurrentYearEquity,
												"expansion",
											),
										)}
									</TableCell>
									<TableCell className="text-right border">
										{formatNumberWithCommas(
											computeSumForField(
												initialNotCurrentYearEquity,
												"accProfitLoss",
											),
										)}
									</TableCell>
									<TableCell className="text-right border">
										{formatNumberWithCommas(
											computeSumForField(initialNotCurrentYearEquity, "other"),
										)}
									</TableCell>
									<TableCell className="text-right border">
										{(() => {
											const total = [
												"registeredCapital",
												"storage",
												"expansion",
												"accProfitLoss",
												"other",
											].reduce(
												(acc, field) =>
													acc +
													parseParenthesisNumber(
														initialNotCurrentYearEquity.reduce(
															(sum, row) =>
																sum + parseParenthesisNumber(row[field]),
															0,
														),
													),
												0,
											);
											return formatNumberWithCommas(total);
										})()}
									</TableCell>
									<TableCell className="text-right border">-</TableCell>

									<TableCell className="text-right border">-</TableCell>
								</TableRow>

								<TableRow className="bg-[#216C4C]/20">
									<TableCell className="border">
										III. ສ່ວນປ່ຽນແປງທຶນເຈົ້າຂອງ ໃນປີ {year}
									</TableCell>
									<TableCell className="text-right border">
										{formatNumberWithCommas(
											computeSumForField(
												initialCurrentYearEquity,
												"registeredCapital",
											),
										)}
									</TableCell>
									<TableCell className="text-right border">
										{formatNumberWithCommas(
											computeSumForField(initialCurrentYearEquity, "storage"),
										)}
									</TableCell>
									<TableCell className="text-right border">
										{formatNumberWithCommas(
											computeSumForField(initialCurrentYearEquity, "expansion"),
										)}
									</TableCell>
									<TableCell className="text-right border">
										{formatNumberWithCommas(
											computeSumForField(
												initialCurrentYearEquity,
												"accProfitLoss",
											),
										)}
									</TableCell>
									<TableCell className="text-right border">
										{formatNumberWithCommas(
											computeSumForField(initialCurrentYearEquity, "other"),
										)}
									</TableCell>
									<TableCell className="text-right border">
										{/* Compute the total of all fields */}
										{(() => {
											const total = [
												"registeredCapital",
												"storage",
												"expansion",
												"accProfitLoss",
												"other",
											].reduce(
												(acc, field) =>
													acc +
													parseParenthesisNumber(
														initialCurrentYearEquity.reduce(
															(sum, row) =>
																sum + parseParenthesisNumber(row[field]),
															0,
														),
													),
												0,
											);
											return formatNumberWithCommas(total);
										})()}
									</TableCell>
									<TableCell className="text-right border">-</TableCell>

									<TableCell className="text-right border">-</TableCell>
								</TableRow>

								{/* Equity Table (Current Year) */}
								{initialCurrentYearEquity?.map((equity, index) => (
									<TableRow key={index}>
										{/* ------ name ------ */}
										<TableCell className="border">
											{[0, 1, 2, 3, 4, 5].includes(index) ? (
												<>{equity.name}</>
											) : (
												<Input
													type="text"
													value={equity.name}
													onChange={(e) => {
														const newRows = [...initialCurrentYearEquity];
														newRows[index].name = e.target.value;
														setInitialCurrentYearEquity(newRows);
													}}
													required
												/>
											)}
										</TableCell>

										{/* ------ registeredCapital ------ */}
										<TableCell className="border">
											<Input
												type="text"
												value={formatNumberWithCommas(equity.registeredCapital)}
												onChange={(e) =>
													handleNumericChange({
														e,
														index,
														fieldName: "registeredCapital",
														data: initialCurrentYearEquity,
														setData: setInitialCurrentYearEquity,
													})
												}
												inputMode="numeric"
												className="text-right"

											// required
											/>
										</TableCell>

										{/* ------ storage ------ */}
										<TableCell className="border">
											<Input
												type="text"
												value={formatNumberWithCommas(equity.storage)}
												onChange={(e) =>
													handleNumericChange({
														e,
														index,
														fieldName: "storage",
														data: initialCurrentYearEquity,
														setData: setInitialCurrentYearEquity,
													})
												}
												inputMode="numeric"
												className="text-right"

											// required
											/>
										</TableCell>

										{/* ------ expansion ------ */}
										<TableCell className="border">
											<Input
												type="text"
												value={formatNumberWithCommas(equity.expansion)}
												onChange={(e) =>
													handleNumericChange({
														e,
														index,
														fieldName: "expansion",
														data: initialCurrentYearEquity,
														setData: setInitialCurrentYearEquity,
													})
												}
												inputMode="numeric"
												className="text-right"

											// required
											/>
										</TableCell>

										{/* ------ accProfitLoss ------ */}
										<TableCell className="border">
											<Input
												type="text"
												value={formatNumberWithCommas(equity.accProfitLoss)}
												onChange={(e) =>
													handleNumericChange({
														e,
														index,
														fieldName: "accProfitLoss",
														data: initialCurrentYearEquity,
														setData: setInitialCurrentYearEquity,
													})
												}
												inputMode="numeric"
												className="text-right"

											// required
											/>
										</TableCell>

										{/* ------ other ------ */}
										<TableCell className="border">
											<Input
												type="text"
												value={formatNumberWithCommas(equity.other)}
												onChange={(e) =>
													handleNumericChange({
														e,
														index,
														fieldName: "other",
														data: initialCurrentYearEquity,
														setData: setInitialCurrentYearEquity,
													})
												}
												inputMode="numeric"
												className="text-right"

											// required
											/>
										</TableCell>

										{/* ------ sum of all fields ------ */}
										<TableCell className="text-right border">
											{computeRowTotal(equity)}
										</TableCell>

										{/* ------ remark ------ */}
										<TableCell className="border">
											<Input
												value={equity.remark}
												onChange={(e) => {
													const newRows = [...initialCurrentYearEquity];
													newRows[index].remark = e.target.value;
													setInitialCurrentYearEquity(newRows);
												}}
											/>
										</TableCell>
									</TableRow>
								))}

								{/* ------ Final row ------ */}
								<TableRow className="bg-[#216C4C]/20">
									<TableCell className="text-center border">
										ຍອດຍັງເຫຼືອ 31 ທັນວາ {year}
									</TableCell>
									{/* Sum of total registered capital current year and not current year */}
									<TableCell className="text-right border">
										{formatNumberWithCommas(
											initialNotCurrentYearEquity.reduce(
												(sum, row) =>
													sum + parseParenthesisNumber(row.registeredCapital),
												0,
											) +
											initialCurrentYearEquity.reduce(
												(sum, row) =>
													sum + parseParenthesisNumber(row.registeredCapital),
												0,
											),
										)}
									</TableCell>

									{/* Sum of total storage current year and not current year */}
									<TableCell className="text-right border">
										{formatNumberWithCommas(
											initialNotCurrentYearEquity.reduce(
												(sum, row) => sum + parseParenthesisNumber(row.storage),
												0,
											) +
											initialCurrentYearEquity.reduce(
												(sum, row) =>
													sum + parseParenthesisNumber(row.storage),
												0,
											),
										)}
									</TableCell>

									{/* Sum of total expansion capital current year and not current year */}
									<TableCell className="text-right border">
										{formatNumberWithCommas(
											initialNotCurrentYearEquity.reduce(
												(sum, row) =>
													sum + parseParenthesisNumber(row.expansion),
												0,
											) +
											initialCurrentYearEquity.reduce(
												(sum, row) =>
													sum + parseParenthesisNumber(row.expansion),
												0,
											),
										)}
									</TableCell>

									{/* Sum of total accProfitLoss current year and not current year */}
									<TableCell className="text-right border">
										{formatNumberWithCommas(
											initialNotCurrentYearEquity.reduce(
												(sum, row) =>
													sum + parseParenthesisNumber(row.accProfitLoss),
												0,
											) +
											initialCurrentYearEquity.reduce(
												(sum, row) =>
													sum + parseParenthesisNumber(row.accProfitLoss),
												0,
											),
										)}
									</TableCell>

									{/* Sum of total other current year and not current year */}
									<TableCell className="text-right border">
										{formatNumberWithCommas(
											initialNotCurrentYearEquity.reduce(
												(sum, row) => sum + parseParenthesisNumber(row.other),
												0,
											) +
											initialCurrentYearEquity.reduce(
												(sum, row) => sum + parseParenthesisNumber(row.other),
												0,
											),
										)}
									</TableCell>

									{/* Sum of all fields */}
									<TableCell className="text-right border">
										{formatNumberWithCommas(
											initialNotCurrentYearEquity.reduce(
												(sum, row) =>
													sum +
													parseParenthesisNumber(row.registeredCapital) +
													parseParenthesisNumber(row.storage) +
													parseParenthesisNumber(row.expansion) +
													parseParenthesisNumber(row.accProfitLoss) +
													parseParenthesisNumber(row.other),
												0,
											) +
											initialCurrentYearEquity.reduce(
												(sum, row) =>
													sum +
													parseParenthesisNumber(row.registeredCapital) +
													parseParenthesisNumber(row.storage) +
													parseParenthesisNumber(row.expansion) +
													parseParenthesisNumber(row.accProfitLoss) +
													parseParenthesisNumber(row.other),
												0,
											),
										)}
									</TableCell>

									<TableCell className="text-right border">-</TableCell>
									<TableCell className="text-right border">-</TableCell>
								</TableRow>
							</TableBody>
						</Table>

						{/* ------------------- File Upload ------------------- */}
						<div className="my-4 w-fit">
							<FileUpload
								currentFile={docFile}
								onFileUpload={(file) => setDocFile(file)}
							/>
						</div>

						{/* ------------------- Action Buttons ------------------- */}
						<div className="flex gap-x-2 justify-end mt-4">
							<Button
								type="button"

								onClick={() => navigate({ to: "/report/finance" })}
								variant={"outline"}
							>
								ຍົກເລີກ
							</Button>
							<Button
								disabled={mutation.isPending}
								type="submit"
								className="flex gap-x-2 items-center"
							>
								{mutation.isPending ? (
									<Spinner className="text-white" />
								) : (
									<>
										<Save className="w-4 h-4" />
										ບັນທຶກ
									</>
								)}
							</Button>
						</div>
					</form>
				</CardContent>
			</Card>
		</section>
	);
}
